import type { ReactNode } from 'react';
import { Dialog, DialogTitle, IconButton, Tooltip, type DialogProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { APP_LABELS } from '../utils/labels';
import SpinnerButton from './SpinnerButton';

interface AppDialogProps extends Omit<DialogProps, 'title'> {
  fullScreenOnMobile?: boolean;
  title?: ReactNode;
  confirmLabel?: string;
  confirmFormId?: string;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  testId?: string;
}

export default function AppDialog({
  fullScreenOnMobile = true,
  title,
  confirmLabel,
  confirmFormId,
  confirmDisabled,
  confirmLoading,
  testId,
  children,
  onClose,
  ...props
}: AppDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isFullScreen = fullScreenOnMobile && isMobile;
  return (
    <Dialog
      {...props}
      data-testid={testId}
      aria-labelledby={title !== undefined ? 'dialog-title' : props['aria-labelledby']}
      onClose={onClose}
      fullScreen={isFullScreen}
      slotProps={{
        ...props.slotProps,
        paper: {
          ...(props.slotProps?.paper as object),
          className: 'flex flex-col',
          sx: {
            // Make <form> children participate in the flex layout so
            // DialogContent scrolls and DialogActions stays pinned at the bottom.
            '& > form': {
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            },
            ...(isFullScreen && confirmLabel
              ? { '& .MuiDialogActions-root': { display: 'none' } }
              : {}),
          },
        },
      }}
    >
      {title !== undefined &&
        (isFullScreen ? (
          <DialogTitle className="flex items-center gap-2 px-6 mt-4 mb-1 py-0">
            {onClose && (
              <Tooltip title={APP_LABELS.close}>
                <IconButton
                  edge="start"
                  onClick={() => onClose({}, 'escapeKeyDown')}
                  aria-label={APP_LABELS.close}
                  color="inherit"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
            <span id="dialog-title" className="flex-1 text-lg font-semibold">
              {title}
            </span>
            {confirmLabel && (
              <SpinnerButton type="submit" form={confirmFormId} disabled={confirmDisabled} loading={confirmLoading} color="primary">
                {confirmLabel}
              </SpinnerButton>
            )}
          </DialogTitle>
        ) : (
          <DialogTitle className="flex items-center gap-2 px-6 mt-4 mb-1 py-0">
            <span id="dialog-title" className="flex-1">
              {title}
            </span>
            {onClose && (
              <Tooltip title={APP_LABELS.close}>
                <IconButton
                  onClick={() => onClose({}, 'escapeKeyDown')}
                  aria-label={APP_LABELS.close}
                  color="inherit"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
          </DialogTitle>
        ))}
      {children}
    </Dialog>
  );
}
